import { Component, OnInit, HostListener } from '@angular/core';
import { QuicksightClient } from '@benefitsui/app/api/benefits-clients';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorService } from '@benefitsui/app/services/http-error/http-error.service';

export const Constants = {
  // Main labels
  MainAriaLabel: 'Claims Report Viewer',
  NavAriaLabel: 'Report navigation',
  
  // Download button and popup
  DownloadButtonLabel: 'Download report',
  DownloadInstructionsLabel: 'Download instructions',
  DownloadInstructionText1: 'To download this report, hover in the top right of the table area and click on the button with 3 dots.',
  DownloadInstructionText2: 'From this menu you may choose a download format: csv or excel.',
  
  // Visual guide
  VisualGuideCaption: 'Visual guide showing the location of the download menu in QuickSight: ' +
    'Screenshot of QuickSight interface highlighting the three-dot menu button in the top right corner of the data table ' +
    'with a hand cursor pointing to it',
  
  // Loading states
  LoadingReportLabel: 'Loading report',
  LoadingClaimsDataLabel: 'Loading claims report data',
  LoadingReportWaitText: 'Loading claims report, please wait...',
  LoadingReportContentLabel: 'Loading report content',
  LoadingReportDataText: 'Loading report data...',
  
  // Report content
  ReportDashboardLabel: 'Claims report dashboard',
  
  // Test content
  TestContentLabel: 'Test content area'
};

@Component({
  selector: 'app-report-viewer',
  templateUrl: './report-viewer.component.html',
  styleUrls: ['./report-viewer.component.scss'],
  standalone: false
})
export class ReportViewerComponent implements OnInit {
  isLoading = false; // Set to false to skip loading state
  isIframeLoading = true; // Track iframe content loading separately
  embedUrl: SafeResourceUrl | null = null;
  dashboardId: string | null = null; // Add dashboardId property for template
  showDownloadPopup = false; // Control download popup visibility

  // Expose constants to template
  readonly Constants = Constants;

  constructor(
    private quicksightClient: QuicksightClient,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private errorService: HttpErrorService
  ) {}

  ngOnInit(): void {
    // Comment out QuickSight loading for testing
    this.loadQuickSightUrl();
    
    // Instead, just get the dashboardId for display
    this.route.queryParams.subscribe((params) => {
      this.dashboardId = params['dashboardId'] || 'No dashboard ID provided';
    });
  }

  private loadQuickSightUrl() {
    this.route.queryParams.subscribe((params) => {
      const dashboardId = params['dashboardId']; // dashboardId from query params

      if (!dashboardId) {
        console.error('No dashboard ID provided');
        this.isLoading = false;
        this.errorService.navigateToError();
        return;
      }

      this.quicksightClient.embedUrl(dashboardId).subscribe({
        next: (response) => {
          if (response.data) {
            this.embedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.data);
          } else {
            console.error('No dashboard data received');
            this.errorService.navigateToError();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading QuickSight URL:', error);
          this.isLoading = false;
        }
      });
    });
  }

  toggleDownloadPopup(): void {
    this.showDownloadPopup = !this.showDownloadPopup;
  }

  onIframeLoad(): void {
    this.isIframeLoading = false;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    const downloadContainer = target.closest('.download-button-container');
    
    // Close popup if clicking outside the download button container
    if (!downloadContainer && this.showDownloadPopup) {
      this.showDownloadPopup = false;
    }
  }

  onDownloadReport(): void {
    // For QuickSight reports, we can trigger the download functionality
    // by sending a message to the embedded iframe or opening the download URL
    if (this.embedUrl) {
      // Get the iframe element and trigger download action
      const iframe = document.querySelector('.report-iframe') as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        try {
          // Send a message to the QuickSight iframe to trigger download
          iframe.contentWindow.postMessage({
            action: 'exportReport',
            format: 'PDF' // or 'CSV', 'XLSX' depending on report type
          }, '*');
        } catch (error) {
          console.warn('Unable to trigger report download via iframe messaging:', error);
          // Fallback: open the report URL in a new tab for manual download
          window.open(this.embedUrl.toString(), '_blank');
        }
      }
    } else {
      console.warn('No report URL available for download');
    }
  }
}
